import { useState } from 'react';
import * as R from 'ramda';
import * as Dfns from 'date-fns/fp';
import { Modal } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker';
import { DateRange, getWeeksFromRange } from '@src/utils/date-utils';

import './ImportModal.scss';

type ImportType = 'bulk' | 'changes' | 'xml';

interface ImportModalProps {
  importType: ImportType;
  importFunction: (file: ArrayBuffer, week: string) => Promise<void>;
  week: string;
  onClose: () => void;
}

const TITLES = {
  bulk: 'Bulk Import (from Excel)',
  xml: 'Bulk XML Process (from XML)',
  changes: 'Import Changes (from Excel)'
};

const DESCRIPTIONS = {
  bulk: 'Bulk import plan rows. If there are imported rows that duplicate existing ones, they are ignored.',
  xml: 'Bulk import from XML. Downloads new file with information for buyer to review. Upload via Bulk Upload when done.',
  changes:
    'Import changes to existing plan rows. What can be changed depends on whether your file has an id column.'
};

export const ImportModal = ({ importType, importFunction, week, onClose }: ImportModalProps) => {
  const [dates, setDates] = useState<DateRange>({ start: week, end: week });
  const [file, setFile] = useState<File | undefined>();
  const [importing, setImporting] = useState(false);

  const importPlan = () => {
    if (!file) {
      return;
    }
    setImporting(true);
    // Create array of weeks to loop over.
    const weeks = getWeeksFromRange(dates);

    // For each week in the range, import the plan.
    const reader = new FileReader();
    reader.onload = async e => {
      const binaryString = e?.target?.result;

      if (binaryString instanceof ArrayBuffer) {
        await Promise.all(weeks.map(importWeek => importFunction(binaryString, importWeek)));
      }

      setImporting(false);
      onClose();
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <Modal show onHide={onClose} className='import-modal'>
      <Modal.Header closeButton>
        <Modal.Title>{TITLES[importType]}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>{DESCRIPTIONS[importType]}</div>
        {importType === 'changes' && <ImportChangesExplainer />}
        <input type='file' onChange={e => setFile(e.target.files?.[0])} />
        <DatePicker
          bordered
          range={dates}
          onChange={newDates => setDates(newDates)}
          isOutsideRange={() => false}
          isDayBlocked={date => !R.pipe(Dfns.parseISO, Dfns.isMonday)(date)}
          fullWeeksOnly
        />
      </Modal.Body>
      <Modal.Footer>
        <Button disabled={!file} loading={importing} onClick={importPlan}>
          Import
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Which fields an import can change depends on how its rows are matched to the plan, which in turn
 * depends on whether the file carries an id column. Both cases are handled automatically, so this
 * just sets expectations before the user picks a file.
 */
const ImportChangesExplainer = () => (
  <div className='explainer'>
    <div className='explainer-case'>
      <span className='explainer-label'>With an id column (a plan exported from this page): </span>
      rows are matched on that id, so everything can be changed except Network, Avail, Type, and
      Campaign.
    </div>
    <div className='explainer-case'>
      <span className='explainer-label'>Without one (a file from the optimizer): </span>
      rows are matched on Rotation, Daypart, Days of Week, and Length, so those can&apos;t be
      changed. Count, cost, notes, market, media classification, impression overrides, and creative
      rotations can.
    </div>
  </div>
);
